import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';
import { ZoomHint, ImageLightbox } from './ZoomableImage';

const API = config.API_URL;
const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB
const ENTITY_TYPES = ['artist', 'event', 'festival', 'member', 'promoter', 'venue'];
const EVENT_FILTER_TYPES = ['artist', 'festival', 'promoter', 'venue'];

// ── ZoomablePreview ───────────────────────────────────────────────────────────
// Image thumbnail with a hover + cursor that opens the shared full-screen
// lightbox (with magnifier). Used in both UploadModal and EditModal wizard
// so the preview is always readable.
function ZoomablePreview({ src, alt = '', style = {} }) {
    const [hovered, setHovered] = useState(false);
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <div
                style={{ position: 'relative', cursor: 'zoom-in', display: 'inline-block', width: '100%' }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => setExpanded(true)}
            >
                <img
                    src={src}
                    alt={alt}
                    style={{
                        width: '100%',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        background: '#111',
                        display: 'block',
                        transition: 'opacity 0.15s ease',
                        opacity: hovered ? 0.82 : 1,
                        ...style,
                    }}
                />
                <ZoomHint visible={hovered} />
            </div>

            {expanded && (
                <ImageLightbox
                    src={src}
                    alt={alt}
                    onClose={() => setExpanded(false)}
                />
            )}
        </>
    );
}

// Upload target options for Step 2 of the wizard
const UPLOAD_TARGETS = [
    { key: 'event_single',   label: 'Event (Single)',   group: 'event' },
    { key: 'event_multiple', label: 'Event (Multiple)', group: 'event' },
    { key: 'artist',         label: 'Artist',           group: 'entity' },
    { key: 'festival',       label: 'Festival',         group: 'entity' },
    { key: 'member',         label: 'Member',           group: 'entity' },
    { key: 'promoter',       label: 'Promoter',         group: 'entity' },
    { key: 'venue',          label: 'Venue',            group: 'entity' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function entityLabel(type) {
    if (!type) return '—';
    return type.charAt(0).toUpperCase() + type.slice(1);
}

// ── Entity Search ─────────────────────────────────────────────────────────────
// Calls GET /search?q=<typed>&type=<entityType> after 350ms debounce,
// shows a dropdown of up to 10 matching records, user clicks to select one.
function EntitySearch({ entityType, value, onChange }) {
    const [query, setQuery]       = useState(value?.name || '');
    const [results, setResults]   = useState([]);
    const [searching, setSearching] = useState(false);
    const [open, setOpen]         = useState(false);
    const [selected, setSelected] = useState(value || null);
    const timerRef    = useRef(null);
    const containerRef = useRef(null);

    // Reset when entityType changes
    useEffect(() => {
        setQuery(''); setResults([]); setSelected(null); setOpen(false);
        onChange(null);
    }, [entityType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close dropdown on outside click
    useEffect(() => {
        const onOut = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const runSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) { setResults([]); setOpen(false); return; }
        setSearching(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${API}/search`, {
                params: { q: q.trim(), type: entityType },
                headers: { Authorization: token },
            });
            setResults(res.data.results || []);
            setOpen(true);
        } catch {
            setResults([]);
        } finally { setSearching(false); }
    }, [entityType]);

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        setSelected(null); onChange(null);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => runSearch(v), 350);
    };

    const handleSelect = (r) => {
        setSelected(r); setQuery(r.name); setOpen(false);
        onChange({ id: r.id, name: r.name });
    };

    const handleClear = () => {
        setQuery(''); setSelected(null); setResults([]); setOpen(false);
        onChange(null);
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={query}
                    onChange={handleChange}
                    onFocus={() => results.length > 0 && !selected && setOpen(true)}
                    placeholder={`Search ${entityLabel(entityType)}s by name…`}
                    style={{ ...S.input, paddingRight: selected ? '32px' : '10px' }}
                />
                {searching && (
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#888' }}>…</span>
                )}
                {selected && !searching && (
                    <button onClick={handleClear} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }} title="Clear">✕</button>
                )}
            </div>

            {/* Results dropdown */}
            {open && results.length > 0 && !selected && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 6px 6px', zIndex: 500, maxHeight: '220px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                    {results.map(r => (
                        <div
                            key={r.id}
                            onMouseDown={() => handleSelect(r)}
                            style={{ padding: '9px 12px', borderBottom: '1px solid #333', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <div style={{ fontSize: '13px', color: '#e0e0e0' }}>{r.name}</div>
                            {r.sub && <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>{r.sub}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* No results message */}
            {open && results.length === 0 && !searching && query.trim().length >= 2 && !selected && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 6px 6px', zIndex: 500, padding: '10px 12px', fontSize: '12px', color: '#888' }}>
                    No {entityLabel(entityType)}s found for "{query}"
                </div>
            )}

            {/* Selected confirmation */}
            {selected && (
                <div style={{ marginTop: '5px', fontSize: '11px', color: '#40916c' }}>
                    ✓ {selected.name}{selected.sub ? ` — ${selected.sub}` : ''}
                </div>
            )}
        </div>
    );
}

// ── Event Results Table ───────────────────────────────────────────────────────
// Shared by Step 4 in both the global wizard and the per-entity Add Photo wizard.
// isMultiple: if true, checkboxes allow multi-select; if false, selecting one clears others.
// byVenue: if true, shows Date/Lineup/Stage columns; if false, shows Date/Venue/City/State/Country/Lineup.
function EventResultsTable({ events, selectedIds, onToggle, isMultiple, byVenue }) {
    const formatEventDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';

    if (events.length === 0) {
        return <div style={{ color: '#666', fontSize: '13px', padding: '12px 0' }}>No events found.</div>;
    }

    const gridCols = byVenue
        ? '24px 110px 1fr 100px'          // ☐ Date Lineup Stage
        : '24px 110px 1fr 70px 60px 60px 1.2fr'; // ☐ Date Venue City State Country Lineup

    return (
        <div style={{ border: '1px solid #333', borderRadius: '6px', overflow: 'hidden', fontSize: '12px', marginTop: '8px' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '6px', padding: '7px 10px', background: '#252525', color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #333' }}>
                <span></span>
                <span>Date</span>
                {byVenue ? (
                    <>
                        <span>Lineup</span>
                        <span>Stage</span>
                    </>
                ) : (
                    <>
                        <span>Venue</span>
                        <span>City</span>
                        <span>State</span>
                        <span>Country</span>
                        <span>Lineup</span>
                    </>
                )}
            </div>
            <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {events.map(e => {
                    const isSelected = selectedIds.includes(e.event_id);
                    return (
                        <div
                            key={e.event_id}
                            onClick={() => onToggle(e, isMultiple)}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: gridCols,
                                gap: '6px',
                                padding: '8px 10px',
                                borderBottom: '1px solid #222',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#2a2040' : 'transparent',
                                color: isSelected ? '#bb86fc' : '#ccc',
                                alignItems: 'center',
                            }}
                            onMouseEnter={ev => { if (!isSelected) ev.currentTarget.style.backgroundColor = '#222'; }}
                            onMouseLeave={ev => { if (!isSelected) ev.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <input
                                type={isMultiple ? 'checkbox' : 'radio'}
                                checked={isSelected}
                                onChange={() => {}}
                                style={{ margin: 0, accentColor: '#bb86fc' }}
                            />
                            <span style={{ whiteSpace: 'nowrap' }}>{formatEventDate(e.event_date)}</span>
                            {byVenue ? (
                                <>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa', fontSize: '11px' }}>{e.artists || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.stage_name || '—'}</span>
                                </>
                            ) : (
                                <>
                                    <span>{e.venue_name || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.city || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.state || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.country || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa', fontSize: '11px' }}>{e.artists || '—'}</span>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            {isMultiple && selectedIds.length > 0 && (
                <div style={{ padding: '6px 10px', background: '#1e1a30', fontSize: '11px', color: '#bb86fc', borderTop: '1px solid #333' }}>
                    ✓ {selectedIds.length} event{selectedIds.length !== 1 ? 's' : ''} selected
                </div>
            )}
        </div>
    );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
// Unassigned images get the full wizard (steps 1–4, skipping capture).
// Already-assigned images keep the simple form.
function EditModal({ image, onClose, onSaved, onDeleted }) {
    const isUnassigned = !image.entity_type;

    // ── Simple form state (assigned images) ──────────────────────────────────
    const [caption, setCaption] = useState(image.caption || '');
    const [entityType, setEntityType] = useState(image.entity_type || '');
    const [entitySearch, setEntitySearch] = useState(
        image.entity_id ? { id: image.entity_id, name: image.entity_name || `${entityLabel(image.entity_type)} #${image.entity_id}` } : null
    );
    const [isPublic, setIsPublic] = useState(image.is_public !== false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');

    // ── Wizard state (unassigned images — steps 1–4, no capture) ────────────
    const [wizStep, setWizStep] = useState(1);
    const [uploadTarget, setUploadTarget] = useState('');
    const [filterEntityType, setFilterEntityType] = useState(EVENT_FILTER_TYPES[0]);
    const [filterEntity, setFilterEntity] = useState(null);
    const [directEntity, setDirectEntity] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsError, setEventsError] = useState('');
    const [eventsRefine, setEventsRefine] = useState('');
    const [selectedEventIds, setSelectedEventIds] = useState([]);
    const [wizCaption, setWizCaption] = useState(image.caption || '');
    const [wizIsPublic, setWizIsPublic] = useState(image.is_public !== false);

    const isEventTarget  = uploadTarget === 'event_single' || uploadTarget === 'event_multiple';
    const isMultipleEvents = uploadTarget === 'event_multiple';

    // Load events when filter entity changes (same logic as UploadModal)
    useEffect(() => {
        if (!filterEntity || !filterEntityType) { setEvents([]); return; }
        setEventsLoading(true); setEventsError(''); setEvents([]); setSelectedEventIds([]);
        (async () => {
            try {
                const endpoint = filterEntityType === 'artist' ? 'artists'
                    : filterEntityType === 'venue' ? 'venues'
                    : filterEntityType === 'festival' ? 'festivals' : 'promoters';
                const res = await axios.get(`${API}/${endpoint}/${filterEntity.id}`);
                let rows = [];
                if (filterEntityType === 'artist' || filterEntityType === 'venue') {
                    rows = (res.data.show_history || []).map(e => ({
                        event_id: e.event_id, event_slug: e.event_slug, event_date: e.event_date,
                        venue_name: e.venue_name, city: e.city || e.venue_city,
                        state: e.state || e.venue_state, country: e.country || e.venue_country,
                        stage_name: e.stage_name,
                        artists: [e.headliners, e.support_acts].filter(Boolean).join(' · ') || e.other_artists || e.festival_name || '—',
                    }));
                } else if (filterEntityType === 'festival') {
                    rows = (res.data.events || []).map(e => ({ event_id: e.event_id, event_slug: e.event_slug, event_date: e.event_date, venue_name: e.venue_name, city: e.city, state: e.state, country: e.country, artists: e.artists || '—' }));
                } else {
                    rows = (res.data.events || []).map(e => ({ event_id: e.event_id, event_slug: e.event_slug, event_date: e.event_date, venue_name: e.venue_name, city: e.venue_city, state: e.venue_state, country: e.venue_country, artists: e.artists || '—' }));
                }
                setEvents(rows);
            } catch { setEventsError('Failed to load events.'); }
            finally { setEventsLoading(false); }
        })();
    }, [filterEntity, filterEntityType]);

    const handleEventToggle = (e, isMultiple) => {
        if (isMultiple) setSelectedEventIds(prev => prev.includes(e.event_id) ? prev.filter(id => id !== e.event_id) : [...prev, e.event_id]);
        else setSelectedEventIds([e.event_id]);
    };

    const refineQ = eventsRefine.trim().toLowerCase();
    const filteredEvents = refineQ ? events.filter(e => [e.event_date, e.venue_name, e.city, e.state, e.country, e.artists].some(v => v && String(v).toLowerCase().includes(refineQ))) : events;

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const token = await getIdToken();
            const res = await axios.patch(`${API}/images/${image.id}`, {
                caption: caption.trim() || null,
                entityType: entityType || null,
                entityId: entitySearch?.id || null,
                isPublic,
            }, { headers: { Authorization: token } });
            onSaved(res.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally { setSaving(false); }
    };

    const handleWizardSave = async () => {
        setSaving(true); setError('');
        try {
            const token = await getIdToken();
            let finalEntityType = null, finalEntityId = null, finalEventIds = null;
            if (isEventTarget) {
                if (isMultipleEvents) {
                    // Multi-event: entity_type/entity_id = the searched-by entity,
                    // eventIds = the selected events to link via image_events
                    finalEntityType = filterEntityType;
                    finalEntityId   = filterEntity?.id;
                    finalEventIds   = selectedEventIds;
                } else {
                    finalEntityType = 'event';
                    finalEntityId   = selectedEventIds[0];
                }
            } else {
                finalEntityType = uploadTarget;
                finalEntityId   = directEntity?.id;
            }
            const res = await axios.patch(`${API}/images/${image.id}`, {
                caption:    wizCaption.trim() || null,
                entityType: finalEntityType || null,
                entityId:   finalEntityId   || null,
                isPublic:   wizIsPublic,
                ...(finalEventIds !== null ? { eventIds: finalEventIds } : {}),
            }, { headers: { Authorization: token } });
            onSaved(res.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        setDeleting(true); setError('');
        try {
            const token = await getIdToken();
            await axios.delete(`${API}/images/${image.id}`, { headers: { Authorization: token } });
            onDeleted(image.id);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Delete failed.');
        } finally { setDeleting(false); }
    };

    const MODAL_WIDTH = wizStep >= 3 ? '1125px' : '650px';

    // ── Wizard UI (unassigned images) ────────────────────────────────────────
    if (isUnassigned) {
        return (
            <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
                <div style={{ ...S.modal, maxWidth: MODAL_WIDTH, transition: 'max-width 0.2s ease' }}>
                    <div style={S.modalHeader}>
                        <span style={{ fontSize: '18px', fontWeight: 600 }}>Assign Photo</span>
                        <button onClick={onClose} style={S.closeBtn}>✕</button>
                    </div>

                    {/* Step indicator */}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', alignItems: 'center' }}>
                        {['Target', 'Search', 'Events', 'Details'].map((label, i) => {
                            const stepNum = i + 1;
                            const active = wizStep === stepNum;
                            const done   = wizStep > stepNum;
                            return (
                                <React.Fragment key={label}>
                                    <div style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: done || active ? '600' : '400', background: active ? '#bb86fc' : done ? '#2d6a4f' : '#2c2c2c', color: active ? '#121212' : done ? '#40916c' : '#666', border: `1px solid ${active ? '#bb86fc' : done ? '#40916c' : '#444'}` }}>
                                        {done ? '✓' : `${stepNum}.`} {label}
                                    </div>
                                    {i < 3 && <div style={{ width: '16px', height: '1px', background: '#333' }} />}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: wizStep >= 3 ? '220px 1fr' : '1fr 1fr', gap: '24px', alignItems: 'start' }}>
                        {/* Left: image preview */}
                        <div>
                            <ZoomablePreview src={image.image_url} alt={image.caption || ''} style={{ maxHeight: '320px' }} />
                        </div>

                        {/* Right: wizard steps */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* Step 1: Target */}
                            {wizStep === 1 && (
                                <>
                                    <div>
                                        <label style={S.label}>What is this photo for?</label>
                                        <select value={uploadTarget} onChange={e => { setUploadTarget(e.target.value); setFilterEntity(null); setDirectEntity(null); setSelectedEventIds([]); }} style={{ ...S.input, marginTop: '6px' }}>
                                            <option value="">— Select a target —</option>
                                            {UPLOAD_TARGETS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                                        </select>
                                    </div>
                                    <button onClick={() => { if (!uploadTarget) return; setWizStep(isEventTarget ? 2 : directEntity ? 4 : 2); }} disabled={!uploadTarget} style={{ ...S.primaryBtn, width: '100%', opacity: !uploadTarget ? 0.5 : 1 }}>Next →</button>
                                </>
                            )}

                            {/* Step 2: Search */}
                            {wizStep === 2 && (
                                <>
                                    {isEventTarget ? (
                                        <div>
                                            <label style={S.label}>Search for events by</label>
                                            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                {EVENT_FILTER_TYPES.map(t => (
                                                    <button key={t} onClick={() => { setFilterEntityType(t); setFilterEntity(null); setEvents([]); }} style={{ ...S.filterChip, ...(filterEntityType === t ? S.filterChipActive : {}), padding: '4px 10px', fontSize: '12px' }}>{entityLabel(t)}</button>
                                                ))}
                                            </div>
                                            <EntitySearch entityType={filterEntityType} value={filterEntity} onChange={setFilterEntity} />
                                            {filterEntity && <div style={{ fontSize: '11px', color: '#40916c', marginTop: '4px' }}>✓ Searching by {entityLabel(filterEntityType)}: {filterEntity.name}</div>}
                                        </div>
                                    ) : (
                                        <div>
                                            <label style={S.label}>Search for {entityLabel(uploadTarget)}</label>
                                            <EntitySearch entityType={uploadTarget} value={directEntity} onChange={setDirectEntity} />
                                            {directEntity && <div style={{ fontSize: '11px', color: '#40916c', marginTop: '4px' }}>✓ Selected: {directEntity.name}</div>}
                                        </div>
                                    )}
                                    <button onClick={() => setWizStep(isEventTarget ? 3 : 4)} disabled={isEventTarget ? !filterEntity : !directEntity} style={{ ...S.primaryBtn, width: '100%', opacity: (isEventTarget ? !filterEntity : !directEntity) ? 0.5 : 1 }}>{isEventTarget ? 'Find Events →' : 'Next →'}</button>
                                    <button onClick={() => setWizStep(1)} style={S.ghostBtn}>← Back</button>
                                </>
                            )}

                            {/* Step 3: Events */}
                            {wizStep === 3 && (
                                <>
                                    <div>
                                        <label style={S.label}>{isMultipleEvents ? 'Select events (multiple allowed)' : 'Select event'}</label>
                                        <input type="text" value={eventsRefine} onChange={e => setEventsRefine(e.target.value)} placeholder="Refine by date, venue, city, artists..." style={{ ...S.input, marginBottom: '4px' }} />
                                        {eventsLoading && <div style={{ color: '#888', fontSize: '13px', padding: '8px 0' }}>Loading events…</div>}
                                        {eventsError && <div style={{ color: '#e57373', fontSize: '13px' }}>{eventsError}</div>}
                                        {!eventsLoading && !eventsError && <EventResultsTable events={filteredEvents} selectedIds={selectedEventIds} onToggle={handleEventToggle} isMultiple={isMultipleEvents} byVenue={filterEntityType === 'venue'} />}
                                    </div>
                                    <button onClick={() => setWizStep(4)} disabled={selectedEventIds.length === 0} style={{ ...S.primaryBtn, width: '100%', opacity: selectedEventIds.length === 0 ? 0.5 : 1 }}>Next →</button>
                                    <button onClick={() => setWizStep(2)} style={S.ghostBtn}>← Back</button>
                                </>
                            )}

                            {/* Step 4: Details */}
                            {wizStep === 4 && (
                                <>
                                    <div style={{ background: '#252525', border: '1px solid #333', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#aaa' }}>
                                        <div style={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px', marginBottom: '4px' }}>Assigning to</div>
                                        {isEventTarget && isMultipleEvents && filterEntity && <div style={{ color: '#e0e0e0' }}><strong>{selectedEventIds.length}</strong> event{selectedEventIds.length !== 1 ? 's' : ''} via {entityLabel(filterEntityType)}: <strong>{filterEntity.name}</strong></div>}
                                        {isEventTarget && !isMultipleEvents && <div style={{ color: '#e0e0e0' }}>Event (Single): event #{selectedEventIds[0]}</div>}
                                        {!isEventTarget && directEntity && <div style={{ color: '#e0e0e0' }}>{entityLabel(uploadTarget)}: <strong>{directEntity.name}</strong></div>}
                                    </div>
                                    <div>
                                        <label style={S.label}>Caption (optional)</label>
                                        <input type="text" value={wizCaption} onChange={e => setWizCaption(e.target.value)} placeholder="Describe this photo..." style={S.input} maxLength={280} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                                        <button onClick={() => setWizIsPublic(v => !v)} style={{ ...S.toggleBtn, backgroundColor: wizIsPublic ? '#2d6a4f' : '#444', borderColor: wizIsPublic ? '#40916c' : '#555' }}>{wizIsPublic ? '🌐 Public' : '🔒 Private'}</button>
                                        <span style={{ fontSize: '12px', color: '#888' }}>{wizIsPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                                    </div>
                                    {error && <div style={{ ...S.errorMsg, marginTop: '4px' }}>{error}</div>}
                                    <button onClick={handleWizardSave} disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1, width: '100%' }}>{saving ? 'Saving…' : '💾 Save Assignment'}</button>
                                    <button onClick={() => setWizStep(isEventTarget ? 3 : 2)} style={S.ghostBtn}>← Back</button>
                                    <div style={{ borderTop: '1px solid #333', paddingTop: '12px' }}>
                                        {!confirmDelete ? <button onClick={() => setConfirmDelete(true)} style={S.deleteBtn}>🗑️ Delete Photo</button> : (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <span style={{ fontSize: '13px', color: '#e57373' }}>Delete permanently?</span>
                                                <button onClick={handleDelete} disabled={deleting} style={{ ...S.deleteBtn, opacity: deleting ? 0.6 : 1 }}>{deleting ? 'Deleting…' : 'Yes, Delete'}</button>
                                                <button onClick={() => setConfirmDelete(false)} style={S.ghostBtn}>Cancel</button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Simple form UI (already-assigned images) ─────────────────────────────
    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ ...S.modal, maxWidth: '650px' }}>
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Edit Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                <div style={S.twoCol}>
                    <div style={S.twoColLeft}>
                        <ZoomablePreview src={image.image_url} alt={image.caption || ''} style={{ maxHeight: '380px' }} />
                    </div>
                    <div style={S.twoColRight}>
                        <div>
                            <label style={S.label}>Caption (optional)</label>
                            <input type="text" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Describe this photo..." style={S.input} maxLength={280} />
                        </div>
                        <div>
                            <label style={S.label}>Assign to (optional)</label>
                            <select value={entityType} onChange={e => { setEntityType(e.target.value); setEntitySearch(null); }} style={{ ...S.input, marginBottom: '6px' }}>
                                <option value="">— None —</option>
                                {ENTITY_TYPES.map(t => <option key={t} value={t}>{entityLabel(t)}</option>)}
                            </select>
                            {entityType && entityType !== 'event' && <EntitySearch entityType={entityType} value={entitySearch} onChange={setEntitySearch} />}
                            {entityType === 'event' && <EntitySearch entityType="event" value={entitySearch} onChange={setEntitySearch} />}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                            <button onClick={() => setIsPublic(v => !v)} style={{ ...S.toggleBtn, backgroundColor: isPublic ? '#2d6a4f' : '#444', borderColor: isPublic ? '#40916c' : '#555' }}>{isPublic ? '🌐 Public' : '🔒 Private'}</button>
                            <span style={{ fontSize: '12px', color: '#888' }}>{isPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                        </div>
                        {error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}
                        <button onClick={handleSave} disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1, marginTop: '12px', width: '100%' }}>{saving ? 'Saving…' : '💾 Save Changes'}</button>
                        <div style={{ borderTop: '1px solid #333', paddingTop: '12px', marginTop: '4px' }}>
                            {!confirmDelete ? <button onClick={() => setConfirmDelete(true)} style={S.deleteBtn}>🗑️ Delete Photo</button> : (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '13px', color: '#e57373' }}>Delete permanently?</span>
                                    <button onClick={handleDelete} disabled={deleting} style={{ ...S.deleteBtn, opacity: deleting ? 0.6 : 1 }}>{deleting ? 'Deleting…' : 'Yes, Delete'}</button>
                                    <button onClick={() => setConfirmDelete(false)} style={S.ghostBtn}>Cancel</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Upload Modal — 5-step wizard ─────────────────────────────────────────────
//
// Entry Point A (global, from My Images): all 5 steps shown.
// Entry Point B (per-entity Add Photo): pre-scoped to entity, starts at Step 1,
//   Step 2 shows only "[Entity] Record" vs "Event (Multiple)",
//   Step 3 (event search) is bypassed for direct-entity choice.
//
// Props:
//   presetEntityType / presetEntityId / presetEntityName — Entry Point B scoping
//   onClose / onUploaded — standard callbacks
//
function UploadModal({ onClose, onUploaded, presetEntityType, presetEntityId, presetEntityName }) {
    const isPreset = !!(presetEntityType && presetEntityId);

    // ── Step state ──────────────────────────────────────────────────────────
    // step: 0=capture, 1=target, 2=search, 3=events, 4=meta+upload
    // For Entry Point B: step 1 shows simplified radio; step 2/3 may be bypassed.
    const [step, setStep] = useState(0);

    // ── Capture state (step 0) ──────────────────────────────────────────────
    const [captureMode, setCaptureMode] = useState('file'); // 'file' | 'camera' | 'preview'
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    // ── Target state (step 1) ──────────────────────────────────────────────
    // For Entry Point B: either '<entityType>_record' or 'event_multiple'
    // For Entry Point A: one of the UPLOAD_TARGETS keys
    const [uploadTarget, setUploadTarget] = useState('none');

    // ── Search state (step 2 — event filter entity OR direct entity) ────────
    const [filterEntityType, setFilterEntityType] = useState(EVENT_FILTER_TYPES[0]);
    const [filterEntity, setFilterEntity] = useState(null); // { id, name }
    // For direct entity (non-event) in Entry Point A: also stored here
    const [directEntity, setDirectEntity] = useState(null); // { id, name }

    // ── Event results + selection (step 3) ─────────────────────────────────
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsError, setEventsError] = useState('');
    const [eventsRefine, setEventsRefine] = useState('');
    const [selectedEventIds, setSelectedEventIds] = useState([]);

    // ── Meta state (step 4) ────────────────────────────────────────────────
    const [caption, setCaption] = useState('');
    const [isPublic, setIsPublic] = useState(true);

    // ── Upload ─────────────────────────────────────────────────────────────
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    // ── Camera helpers ─────────────────────────────────────────────────────
    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch {
            setError('Camera access denied or unavailable.');
            setCaptureMode('file');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    }, []);

    useEffect(() => {
        if (captureMode === 'camera') startCamera();
        else stopCamera();
        return stopCamera;
    }, [captureMode, startCamera, stopCamera]);

    const capturePhoto = () => {
        const video = videoRef.current; const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth; canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        canvas.toBlob(blob => {
            if (!blob) return;
            const f = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setFile(f); setPreview(URL.createObjectURL(f)); stopCamera(); setCaptureMode('preview');
            setStep(1);
        }, 'image/jpeg', 0.92);
    };

    const handleFileSelect = (f) => {
        if (!f) return;
        if (f.size > MAX_FILE_BYTES) { setError('File exceeds 4 MB limit.'); return; }
        if (!f.type.startsWith('image/')) { setError('Only image files are allowed.'); return; }
        setError(''); setFile(f); setPreview(URL.createObjectURL(f)); setCaptureMode('preview');
        setStep(1);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFileSelect(e.dataTransfer.files?.[0]);
    };

    // ── Event loading (triggered when filterEntity is selected in step 2) ──
    useEffect(() => {
        // Entry Point B: when event_multiple chosen and preset entity is known
        const entityForEvents = filterEntity || (isPreset && uploadTarget === 'event_multiple' ? { id: presetEntityId, name: presetEntityName } : null);
        const entityTypeForEvents = filterEntity ? filterEntityType : (isPreset ? presetEntityType : null);

        if (!entityForEvents || !entityTypeForEvents) { setEvents([]); return; }

        setEventsLoading(true); setEventsError(''); setEvents([]); setSelectedEventIds([]);

        (async () => {
            try {
                const endpoint = entityTypeForEvents === 'artist' ? 'artists'
                    : entityTypeForEvents === 'venue' ? 'venues'
                    : entityTypeForEvents === 'festival' ? 'festivals'
                    : 'promoters';
                const res = await axios.get(`${API}/${endpoint}/${entityForEvents.id}`);
                let rows = [];
                if (entityTypeForEvents === 'artist' || entityTypeForEvents === 'venue') {
                    rows = (res.data.show_history || []).map(e => ({
                        event_id:   e.event_id,
                        event_slug: e.event_slug,
                        event_date: e.event_date,
                        venue_name: e.venue_name,
                        city:       e.city || e.venue_city,
                        state:      e.state || e.venue_state,
                        country:    e.country || e.venue_country,
                        stage_name: e.stage_name,
                        artists:    [e.headliners, e.support_acts].filter(Boolean).join(' · ') || e.other_artists || e.festival_name || '—',
                    }));
                } else if (entityTypeForEvents === 'festival') {
                    rows = (res.data.events || []).map(e => ({
                        event_id:   e.event_id,
                        event_slug: e.event_slug,
                        event_date: e.event_date,
                        venue_name: e.venue_name,
                        city:       e.city,
                        state:      e.state,
                        country:    e.country,
                        artists:    e.artists || '—',
                    }));
                } else if (entityTypeForEvents === 'promoter') {
                    rows = (res.data.events || []).map(e => ({
                        event_id:   e.event_id,
                        event_slug: e.event_slug,
                        event_date: e.event_date,
                        venue_name: e.venue_name,
                        city:       e.venue_city,
                        state:      e.venue_state,
                        country:    e.venue_country,
                        artists:    e.artists || '—',
                    }));
                }
                setEvents(rows);
            } catch {
                setEventsError('Failed to load events.');
            } finally {
                setEventsLoading(false);
            }
        })();
    }, [filterEntity, filterEntityType, uploadTarget, isPreset, presetEntityId, presetEntityName, presetEntityType]);

    // ── Event selection toggle ──────────────────────────────────────────────
    const handleEventToggle = (e, isMultiple) => {
        if (isMultiple) {
            setSelectedEventIds(prev =>
                prev.includes(e.event_id) ? prev.filter(id => id !== e.event_id) : [...prev, e.event_id]
            );
        } else {
            setSelectedEventIds([e.event_id]);
        }
    };

    // ── Step navigation ────────────────────────────────────────────────────
    const isEventTarget = uploadTarget === 'event_single' || uploadTarget === 'event_multiple';
    const isMultipleEvents = uploadTarget === 'event_multiple';

    // For Entry Point B: when event_multiple is chosen, load events immediately using preset entity
    const isPresetMultiEvent = isPreset && uploadTarget === 'event_multiple';

    // ── Upload handler ─────────────────────────────────────────────────────
    const toBase64 = f => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
    });

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true); setError('');
        try {
            const token = await getIdToken();
            const data = await toBase64(file);
            const uploadRes = await axios.post(`${API}/images/upload`,
                { filename: file.name, contentType: file.type, data },
                { headers: { Authorization: token } }
            );
            const { s3Key } = uploadRes.data;

            // Determine entityType/entityId and optional eventIds
            let entityType = null, entityId = null, eventIds = null;

            if (isPreset) {
                // Entry Point B
                if (uploadTarget === 'event_multiple') {
                    entityType = presetEntityType;
                    entityId   = presetEntityId;
                    eventIds   = selectedEventIds;
                } else {
                    entityType = presetEntityType;
                    entityId   = presetEntityId;
                }
            } else if (uploadTarget === 'none') {
                // Unassigned — no entity
                entityType = null; entityId = null;
            } else if (isEventTarget) {
                // Entry Point A, event target
                if (isMultipleEvents) {
                    // Searched-by entity is filterEntity (the artist/venue/festival/promoter used to find events)
                    entityType = filterEntityType;
                    entityId   = filterEntity?.id;
                    eventIds   = selectedEventIds;
                } else {
                    // Single event: entity_type = 'event', entity_id = the chosen event
                    entityType = 'event';
                    entityId   = selectedEventIds[0];
                }
            } else {
                // Entry Point A, direct entity (artist/festival/member/promoter/venue)
                entityType = uploadTarget;
                entityId   = directEntity?.id;
            }

            const createRes = await axios.post(`${API}/images`, {
                s3Key,
                caption:    caption.trim() || null,
                entityType: entityType || null,
                entityId:   entityId   || null,
                isPublic,
                eventIds:   eventIds   || null,
            }, { headers: { Authorization: token } });

            onUploaded(createRes.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    // ── Refined event list ─────────────────────────────────────────────────
    const refineQ = eventsRefine.trim().toLowerCase();
    const filteredEvents = refineQ
        ? events.filter(e => [e.event_date, e.venue_name, e.city, e.state, e.country, e.artists]
            .some(v => v && String(v).toLowerCase().includes(refineQ)))
        : events;

    // ── Step labels for progress indicator ────────────────────────────────
    const stepLabels = isPreset
        ? ['Upload', 'Target', 'Events', 'Details']
        : ['Upload', 'Target', 'Search', 'Events', 'Details'];

    // ── Modal width — fixed at 650px (steps 0-2), 1125px (steps 3+) ──────────
    // 650 = 520 * 1.25 rounded, 1125 = 900 * 1.25
    const modalWidth = step >= 3 ? '1125px' : '650px';

    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ ...S.modal, maxWidth: modalWidth, transition: 'max-width 0.2s ease' }}>

                {/* Header */}
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Upload Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                {/* Step indicator */}
                {file && (
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', alignItems: 'center' }}>
                        {stepLabels.map((label, i) => {
                            // Map visual step index to internal step state
                            // Entry Point B: Upload(0), Target(1), Events(2 if multi), Details(3/2)
                            // Entry Point A: Upload(0), Target(1), Search(2), Events(3), Details(4)
                            const active = i === step;
                            const done   = i < step;
                            return (
                                <React.Fragment key={label}>
                                    <div style={{
                                        padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: done || active ? '600' : '400',
                                        background: active ? '#bb86fc' : done ? '#2d6a4f' : '#2c2c2c',
                                        color: active ? '#121212' : done ? '#40916c' : '#666',
                                        border: `1px solid ${active ? '#bb86fc' : done ? '#40916c' : '#444'}`,
                                    }}>
                                        {done ? '✓' : `${i + 1}.`} {label}
                                    </div>
                                    {i < stepLabels.length - 1 && <div style={{ width: '16px', height: '1px', background: '#333' }} />}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                {/* ── Step 0: Capture ─────────────────────────────────────── */}
                {step === 0 && (
                    <>
                        {captureMode !== 'preview' && (
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                                <button onClick={() => setCaptureMode('file')} style={{ ...S.modeBtn, ...(captureMode === 'file' ? S.modeBtnActive : {}) }}>📁 From Device</button>
                                <button onClick={() => setCaptureMode('camera')} style={{ ...S.modeBtn, ...(captureMode === 'camera' ? S.modeBtnActive : {}) }}>📷 Camera</button>
                            </div>
                        )}
                        {captureMode === 'file' && (
                            <div style={S.dropZone} onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => fileInputRef.current?.click()}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</div>
                                <div style={{ color: '#aaa', fontSize: '14px' }}>Drag & drop or click to select</div>
                                <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>JPEG, PNG, GIF, WEBP · Max 4 MB</div>
                                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files?.[0])} />
                            </div>
                        )}
                        {captureMode === 'camera' && (
                            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'cover' }} />
                                <canvas ref={canvasRef} style={{ display: 'none' }} />
                                <button onClick={capturePhoto} style={{ ...S.primaryBtn, marginTop: '10px' }}>📸 Capture Photo</button>
                            </div>
                        )}
                        {error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}
                    </>
                )}

                {/* Steps 1–4: two-column layout with preview on left */}
                {step >= 1 && file && (
                    <div style={{ display: 'grid', gridTemplateColumns: step >= 3 ? '220px 1fr' : '1fr 1fr', gap: '24px', alignItems: 'start' }}>

                        {/* Left: image preview (always visible after capture) */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <ZoomablePreview src={preview} alt="Preview" style={{ maxHeight: '320px' }} />
                            <button onClick={() => { setFile(null); setPreview(null); setCaptureMode('file'); setStep(0); setUploadTarget('none'); setFilterEntity(null); setDirectEntity(null); setSelectedEventIds([]); }} style={{ ...S.ghostBtn, marginTop: '8px', fontSize: '12px', width: '100%' }}>
                                ← Choose different photo
                            </button>
                        </div>

                        {/* Right: wizard steps */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* ── Step 1: Target selection ──────────────────── */}
                            {step === 1 && (
                                <>
                                    <div>
                                        <label style={S.label}>What is this photo for?</label>
                                        {isPreset ? (
                                            // Entry Point B: only two options, keep as compact select
                                            <select
                                                value={uploadTarget}
                                                onChange={e => { setUploadTarget(e.target.value); setFilterEntity(null); setDirectEntity(null); setSelectedEventIds([]); }}
                                                style={{ ...S.input, marginTop: '6px' }}
                                            >
                                                <option value="">— Select a target —</option>
                                                <option value={`${presetEntityType}_record`}>{entityLabel(presetEntityType)} Record</option>
                                                <option value="event_multiple">Event (Multiple)</option>
                                            </select>
                                        ) : (
                                            // Entry Point A: 8 options including --None--
                                            <select
                                                value={uploadTarget}
                                                onChange={e => { setUploadTarget(e.target.value); setFilterEntity(null); setDirectEntity(null); setSelectedEventIds([]); }}
                                                style={{ ...S.input, marginTop: '6px' }}
                                            >
                                                <option value="none">— None (upload without assigning) —</option>
                                                {UPLOAD_TARGETS.map(t => (
                                                    <option key={t.key} value={t.key}>{t.label}</option>
                                                ))}
                                            </select>
                                        )}
                                        {uploadTarget && uploadTarget !== 'none' && (
                                            <div style={{ fontSize: '11px', color: '#bb86fc', marginTop: '6px' }}>
                                                ✓ {isPreset
                                                    ? (uploadTarget === 'event_multiple' ? 'Select events to link to this ' + entityLabel(presetEntityType) : 'Assign directly to this ' + entityLabel(presetEntityType))
                                                    : UPLOAD_TARGETS.find(t => t.key === uploadTarget)?.label}
                                            </div>
                                        )}
                                        {uploadTarget === 'none' && (
                                            <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                                Photo will be uploaded without assignment — you can assign it later via Edit.
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (!uploadTarget) return;
                                            if (uploadTarget === 'none') {
                                                // Skip to meta — no entity assignment
                                                setStep(4);
                                            } else if (isPreset && uploadTarget !== 'event_multiple') {
                                                // Direct entity record — skip to meta
                                                setStep(4);
                                            } else if (isPreset && uploadTarget === 'event_multiple') {
                                                // Preset multi-event — skip to events
                                                setStep(3);
                                            } else {
                                                setStep(2);
                                            }
                                        }}
                                        disabled={false}
                                        style={{ ...S.primaryBtn, width: '100%' }}
                                    >
                                        Next →
                                    </button>
                                </>
                            )}

                            {/* ── Step 2: Search (Entry Point A only) ─────────── */}
                            {step === 2 && !isPreset && (
                                <>
                                    {isEventTarget ? (
                                        <>
                                            <div>
                                                <label style={S.label}>Search for events by</label>
                                                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                    {EVENT_FILTER_TYPES.map(t => (
                                                        <button key={t} onClick={() => { setFilterEntityType(t); setFilterEntity(null); setEvents([]); }} style={{ ...S.filterChip, ...(filterEntityType === t ? S.filterChipActive : {}), padding: '4px 10px', fontSize: '12px' }}>
                                                            {entityLabel(t)}
                                                        </button>
                                                    ))}
                                                </div>
                                                <EntitySearch entityType={filterEntityType} value={filterEntity} onChange={setFilterEntity} />
                                                {filterEntity && <div style={{ fontSize: '11px', color: '#40916c', marginTop: '4px' }}>✓ Searching by {entityLabel(filterEntityType)}: {filterEntity.name}</div>}
                                            </div>
                                            <button onClick={() => setStep(3)} disabled={!filterEntity} style={{ ...S.primaryBtn, width: '100%', opacity: !filterEntity ? 0.5 : 1 }}>
                                                Find Events →
                                            </button>
                                        </>
                                    ) : (
                                        // Direct entity (Artist/Festival/Member/Promoter/Venue)
                                        <>
                                            <div>
                                                <label style={S.label}>Search for {entityLabel(uploadTarget)}</label>
                                                <EntitySearch entityType={uploadTarget} value={directEntity} onChange={setDirectEntity} />
                                                {directEntity && <div style={{ fontSize: '11px', color: '#40916c', marginTop: '4px' }}>✓ Selected: {directEntity.name}</div>}
                                            </div>
                                            <button onClick={() => setStep(4)} disabled={!directEntity} style={{ ...S.primaryBtn, width: '100%', opacity: !directEntity ? 0.5 : 1 }}>
                                                Next →
                                            </button>
                                        </>
                                    )}
                                    <button onClick={() => setStep(1)} style={S.ghostBtn}>← Back</button>
                                </>
                            )}

                            {/* ── Step 3: Event results + selection ────────────── */}
                            {step === 3 && (
                                <>
                                    <div>
                                        <label style={S.label}>
                                            {isMultipleEvents || isPresetMultiEvent
                                                ? 'Select events (multiple allowed)'
                                                : 'Select event'}
                                        </label>

                                        {/* Refine filter */}
                                        <input
                                            type="text"
                                            value={eventsRefine}
                                            onChange={e => setEventsRefine(e.target.value)}
                                            placeholder="Refine by date, venue, city, artists..."
                                            style={{ ...S.input, marginBottom: '4px' }}
                                        />

                                        {eventsLoading && <div style={{ color: '#888', fontSize: '13px', padding: '8px 0' }}>Loading events…</div>}
                                        {eventsError && <div style={{ color: '#e57373', fontSize: '13px' }}>{eventsError}</div>}

                                        {!eventsLoading && !eventsError && (
                                            <EventResultsTable
                                                events={filteredEvents}
                                                selectedIds={selectedEventIds}
                                                onToggle={handleEventToggle}
                                                isMultiple={isMultipleEvents || isPresetMultiEvent}
                                                byVenue={
                                                    (filterEntityType === 'venue') ||
                                                    (isPreset && presetEntityType === 'venue')
                                                }
                                            />
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setStep(4)}
                                        disabled={selectedEventIds.length === 0}
                                        style={{ ...S.primaryBtn, width: '100%', opacity: selectedEventIds.length === 0 ? 0.5 : 1 }}
                                    >
                                        Next →
                                    </button>
                                    <button onClick={() => setStep(isPreset ? 1 : 2)} style={S.ghostBtn}>← Back</button>
                                </>
                            )}

                            {/* ── Step 4: Caption + visibility + upload ─────── */}
                            {step === 4 && (
                                <>
                                    {/* Summary of what this image will be assigned to */}
                                    <div style={{ background: '#252525', border: '1px solid #333', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#aaa' }}>
                                        <div style={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px', marginBottom: '4px' }}>Assigning to</div>
                                        {uploadTarget === 'none' && (
                                            <div style={{ color: '#777', fontStyle: 'italic' }}>Not assigned — you can assign this photo later via Edit.</div>
                                        )}
                                        {isPreset && uploadTarget !== 'event_multiple' && (
                                            <div style={{ color: '#e0e0e0' }}>{entityLabel(presetEntityType)}: <strong>{presetEntityName}</strong></div>
                                        )}
                                        {isPreset && uploadTarget === 'event_multiple' && (
                                            <div style={{ color: '#e0e0e0' }}>
                                                <strong>{selectedEventIds.length}</strong> event{selectedEventIds.length !== 1 ? 's' : ''} via {entityLabel(presetEntityType)}: <strong>{presetEntityName}</strong>
                                            </div>
                                        )}
                                        {!isPreset && uploadTarget !== 'none' && isEventTarget && isMultipleEvents && filterEntity && (
                                            <div style={{ color: '#e0e0e0' }}>
                                                <strong>{selectedEventIds.length}</strong> event{selectedEventIds.length !== 1 ? 's' : ''} via {entityLabel(filterEntityType)}: <strong>{filterEntity.name}</strong>
                                            </div>
                                        )}
                                        {!isPreset && uploadTarget !== 'none' && isEventTarget && !isMultipleEvents && (
                                            <div style={{ color: '#e0e0e0' }}>Event (Single): event #{selectedEventIds[0]}</div>
                                        )}
                                        {!isPreset && uploadTarget !== 'none' && !isEventTarget && directEntity && (
                                            <div style={{ color: '#e0e0e0' }}>{entityLabel(uploadTarget)}: <strong>{directEntity.name}</strong></div>
                                        )}
                                    </div>

                                    {/* Caption */}
                                    <div>
                                        <label style={S.label}>Caption (optional)</label>
                                        <input type="text" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Describe this photo..." style={S.input} maxLength={280} />
                                    </div>

                                    {/* Visibility */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                                        <button onClick={() => setIsPublic(v => !v)} style={{ ...S.toggleBtn, backgroundColor: isPublic ? '#2d6a4f' : '#444', borderColor: isPublic ? '#40916c' : '#555' }}>
                                            {isPublic ? '🌐 Public' : '🔒 Private'}
                                        </button>
                                        <span style={{ fontSize: '12px', color: '#888' }}>{isPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                                    </div>

                                    {error && <div style={{ ...S.errorMsg, marginTop: '4px' }}>{error}</div>}

                                    <button onClick={handleUpload} disabled={uploading} style={{ ...S.primaryBtn, opacity: uploading ? 0.6 : 1, width: '100%' }}>
                                        {uploading ? 'Uploading…' : '⬆️  Upload Photo'}
                                    </button>
                                    <button onClick={() => {
                                        // Back: none or preset direct → step 1; event → step 3; direct entity → step 2
                                        if (uploadTarget === 'none') setStep(1);
                                        else if (isPreset && uploadTarget === 'event_multiple') setStep(3);
                                        else if (!isPreset && isEventTarget) setStep(3);
                                        else if (!isPreset && !isEventTarget) setStep(2);
                                        else setStep(1);
                                    }} style={S.ghostBtn}>← Back</button>
                                </>
                            )}

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyImages() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showUpload, setShowUpload] = useState(false);
    const [editImage, setEditImage] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [lightboxImg, setLightboxImg] = useState(null);
    const [hoveredThumb, setHoveredThumb] = useState(null);

    const loadImages = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const token = await getIdToken();
            const res = await axios.get(`${API}/images/mine`, { headers: { Authorization: token } });
            setImages(res.data.images || []);
        } catch {
            setError('Failed to load images.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadImages(); }, [loadImages]);

    const handleUploaded = (img) => setImages(prev => [img, ...prev]);
    const handleSaved    = (img) => setImages(prev => prev.map(i => i.id === img.id ? { ...img, image_url: i.image_url } : i));
    const handleDeleted  = (id)  => setImages(prev => prev.filter(i => i.id !== id));

    const filtered = images.filter(img => {
        if (filterType === 'all') return true;
        if (filterType === 'unassigned') return !img.entity_type;
        return img.entity_type === filterType;
    });

    const counts = {
        all: images.length,
        unassigned: images.filter(i => !i.entity_type).length,
        ...Object.fromEntries(ENTITY_TYPES.map(t => [t, images.filter(i => i.entity_type === t).length])),
    };

    return (
        <div style={S.page}>
            <div style={S.pageHeader}>
                <h1 style={S.pageTitle}>My Images</h1>
                <button onClick={() => setShowUpload(true)} style={S.primaryBtn}>+ Upload Photo</button>
            </div>

            <div style={S.filterBar}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'unassigned', label: 'Unassigned' },
                    ...ENTITY_TYPES.map(t => ({ key: t, label: entityLabel(t) + 's' })),
                ].map(({ key, label }) => (
                    counts[key] > 0 || key === 'all' ? (
                        <button key={key} onClick={() => setFilterType(key)} style={{ ...S.filterChip, ...(filterType === key ? S.filterChipActive : {}) }}>
                            {label}<span style={S.chipCount}>{counts[key] || 0}</span>
                        </button>
                    ) : null
                ))}
            </div>

            {loading && <div style={S.empty}>Loading…</div>}
            {!loading && error && <div style={S.errorMsg}>{error}</div>}
            {!loading && !error && filtered.length === 0 && (
                <div style={S.empty}>
                    {filterType === 'all' ? 'No photos yet. Click "Upload Photo" to get started!' : `No ${filterType === 'unassigned' ? 'unassigned' : filterType} photos.`}
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div style={S.grid}>
                    {filtered.map(img => (
                        <div key={img.id} style={S.card}>
                            <div
                                style={S.thumb}
                                onClick={() => setLightboxImg(img)}
                                onMouseEnter={() => setHoveredThumb(img.id)}
                                onMouseLeave={() => setHoveredThumb(null)}
                                title="View full size"
                            >
                                <img src={img.image_url} alt={img.caption || ''} style={S.thumbImg} loading="lazy" />
                                {!img.is_public && <span style={S.privateBadge}>🔒</span>}
                                <ZoomHint visible={hoveredThumb === img.id} />
                            </div>
                            <div style={S.cardBody}>
                                {img.caption && <div style={S.caption} title={img.caption}>{img.caption}</div>}
                                <div style={S.cardMeta}>
                                    {img.entity_type ? (
                                        <span style={S.entityBadge}>
                                            {entityLabel(img.entity_type)}: {img.entity_name || `#${img.entity_id}`}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#555', fontSize: '12px' }}>Unassigned</span>
                                    )}
                                    <span style={{ color: '#555', fontSize: '11px' }}>{formatDate(img.created_at)}</span>
                                </div>
                                <button onClick={() => setEditImage(img)} style={S.editBtn}>Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {lightboxImg && (
                <ImageLightbox
                    src={lightboxImg.image_url}
                    alt={lightboxImg.caption || ''}
                    caption={lightboxImg.caption}
                    onClose={() => setLightboxImg(null)}
                />
            )}

            {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />}
            {editImage && <EditModal image={editImage} onClose={() => setEditImage(null)} onSaved={handleSaved} onDeleted={handleDeleted} />}
        </div>
    );
}

// Export UploadModal so detail pages can use it inline
export { UploadModal };

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#e0e0e0', fontFamily: 'inherit' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
    pageTitle: { fontSize: '24px', fontWeight: 700, margin: 0, color: '#e0e0e0' },
    filterBar: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
    filterChip: { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', border: '1px solid #444', background: '#2c2c2c', color: '#aaa', fontSize: '13px', cursor: 'pointer' },
    filterChipActive: { borderColor: '#bb86fc', background: '#2a2040', color: '#bb86fc' },
    chipCount: { background: '#333', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', color: '#888' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
    card: { background: '#1e1e1e', border: '1px solid #333', borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    thumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    thumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', fontSize: '14px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px' },
    cardBody: { padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
    caption: { fontSize: '13px', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    cardMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
    entityBadge: { fontSize: '11px', background: '#2a2040', color: '#bb86fc', border: '1px solid #3d3060', borderRadius: '4px', padding: '2px 6px' },
    editBtn: { marginTop: '4px', padding: '5px 10px', background: 'transparent', border: '1px solid #444', borderRadius: '5px', color: '#aaa', fontSize: '12px', cursor: 'pointer', alignSelf: 'flex-end' },
    empty: { textAlign: 'center', color: '#666', marginTop: '60px', fontSize: '15px' },
    // Modal
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' },
    modal: { background: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', padding: '24px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    closeBtn: { background: 'transparent', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' },
    twoColLeft: { display: 'flex', flexDirection: 'column' },
    twoColRight: { display: 'flex', flexDirection: 'column', gap: '4px' },
    dropZone: { border: '2px dashed #444', borderRadius: '10px', padding: '36px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px' },
    label: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { width: '100%', background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box' },
    primaryBtn: { background: '#bb86fc', color: '#1a1a1a', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
    ghostBtn: { background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    deleteBtn: { background: 'transparent', border: '1px solid #e57373', borderRadius: '6px', color: '#e57373', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    toggleBtn: { border: '1px solid', borderRadius: '20px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', color: '#e0e0e0' },
    modeBtn: { flex: 1, background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#aaa', padding: '8px', fontSize: '13px', cursor: 'pointer' },
    modeBtnActive: { borderColor: '#bb86fc', color: '#bb86fc', background: '#2a2040' },
    errorMsg: { color: '#e57373', background: '#2a1a1a', border: '1px solid #6b2c2c', borderRadius: '6px', padding: '8px 12px', fontSize: '13px' },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '10px 14px', fontSize: '14px', borderRadius: '0 0 8px 8px', textAlign: 'center' },
    eventTable: { border: '1px solid #333', borderRadius: '6px', overflow: 'hidden', fontSize: '12px' },
    eventTableHeader: { display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1.5fr', gap: '8px', padding: '8px 10px', background: '#252525', color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #333' },
    eventRow: { display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1.5fr', gap: '8px', padding: '8px 10px', borderBottom: '1px solid #222', cursor: 'pointer', backgroundColor: 'transparent', color: '#ccc' },
    eventRowSelected: { backgroundColor: '#2a2040', color: '#bb86fc' },
};
